import { useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { Check, ChevronRight, Eye, Pencil, Plus, UserPlus, Settings2 } from "lucide-react";
import { Collapse } from "@/components/design/Collapse";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Switch } from "@/components/design/Switch";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import type { AccessRoleDetailsFragment } from "@/graphql/generated/operations";
import { AccessPermissionDescription } from "./AccessPermissionDescription";
import { permissionLabel } from "./access-permission-labels";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";

type Permission = AccessRoleDetailsFragment["permissions"][number];
type FeatureNode = { key: string; permission?: Permission; children: FeatureNode[] };
const FEATURE_ORDER = ["save", "save.profile", "save.profile.powers-source", "save.profile.powers-source.self",
  "save.profile.powers-source.smart", "save.profile.powers-source.combined", "save.documents", "save.community", "save.pins",
  "grow", "grow.map", "grow.play", "grow.play.self-assessments", "grow.play.games", "grow.play.games.toplist",
  "grow.play.games.roleplay", "grow.watch", "share", "match", "tools", "search", "assistant", "chat"];

// A parent whose children replaced it: the overview shows it as their heading, with no switch of its own,
// while genesis still carries the Permission (save.profile.powers-source until its lenses are deployed).
const HEADING_ONLY = new Set(["feature:save.profile.powers-source"]);
// Labels of controls inside a page, which have no sidebar entry to borrow a word from (feature-catalog.ts).
const CATALOG_LABELS = new Map<string, { id: string; defaultMessage: string }>(
  Object.values(FEATURE_CATALOG).flatMap((entry) => ("label" in entry ? [[entry.key, entry.label]] : [])));
// What a group of alternatives falls back to when none is switched on, shown beneath them.
const FEATURE_HINTS: Record<string, string> = { "save.profile.powers-source": "admin.roles.layout.featureHint.powersSource" };

function featureTree(permissions: Permission[]) {
  const nodes = new Map<string, FeatureNode>();
  const ensure = (key: string): FeatureNode => {
    const existing = nodes.get(key);
    if (existing) return existing;
    const node: FeatureNode = { key, children: [] }; nodes.set(key, node);
    return node;
  };
  for (const permission of permissions) {
    const key = permission.code.slice("feature:".length);
    ensure(key).permission = permission;
    const parts = key.split(".");
    while (parts.length > 1) { parts.pop(); ensure(parts.join(".")); }
    if (["search", "assistant", "chat"].includes(key)) ensure("tools");
  }
  const roots: FeatureNode[] = [];
  for (const node of nodes.values()) {
    const parent = ["search", "assistant", "chat"].includes(node.key) ? "tools"
      : node.key.includes(".") ? node.key.slice(0, node.key.lastIndexOf(".")) : null;
    if (parent) ensure(parent).children.push(node); else roots.push(node);
  }
  const rank = (key: string) => FEATURE_ORDER.includes(key) ? FEATURE_ORDER.indexOf(key) : FEATURE_ORDER.length;
  const sort = (entries: FeatureNode[]) => { entries.sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key)); entries.forEach(node => sort(node.children)); };
  sort(roots); return roots;
}

function permissionSection(code: string) {
  if (code.startsWith("feature:")) return "features";
  const resource = code.split(":")[1];
  if (code === "read" || ["person", "person-reference", "external-information", "managed-information", "invitee"].includes(resource ?? "")) return "people";
  if (resource === "organizational-record") return "records";
  if (resource === "company") return "companies";
  if (resource === "public-content") return "content";
  if (["access-config", "import", "questionnaire", "self-assessment", "reference-data"].includes(resource ?? "")) return "administration";
  return "other";
}

const ACTION_ICONS = { read: Eye, write: Pencil, create: Plus, provision: UserPlus };
const ACTION_ORDER = ["read", "create", "write", "provision"];

export function AccessRolePermissions({ permissions, selected, onToggle, restrictions = {}, expandAll = false, renderDetails }: {
  renderDetails?: (permission: Permission) => ReactNode;
  permissions: Permission[]; restrictions?: Record<string, string>; selected?: string[]; onToggle?: (id: string) => void; expandAll?: boolean;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [view, setView] = useState<"simple" | "expert">("simple");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["save", "grow", "tools"]));
  const features = featureTree(permissions.filter(permission => permissionSection(permission.code) === "features"
    && !HEADING_ONLY.has(permission.code)));
  const checked = (permission: Permission) => !onToggle || !!selected?.includes(permission.id);
  const featureLabel = (node: FeatureNode) => {
    const id = `admin.roles.layout.feature.${node.key}`;
    const catalogLabel = CATALOG_LABELS.get(node.key);
    return id in intl.messages ? t(id) : catalogLabel ? intl.formatMessage(catalogLabel)
      : node.permission ? permissionLabel(intl, node.permission.code, node.permission.description) : node.key;
  };
  const featureRow = (node: FeatureNode, depth = 0) => {
    const open = expandAll || expanded.has(node.key);
    return <div key={node.key}>
      <div className={`flex min-h-12 items-center justify-between gap-3 py-2 pr-3 ${depth === 0 ? "border-t border-ink/10" : ""}`}>
        <div className="flex min-w-0 items-center gap-2" style={{ paddingLeft: depth * 20 }}>
          {node.children.length ? <button id={`roles-feature-expand-${node.key}`} type="button" aria-expanded={open}
            aria-label={featureLabel(node)} className="flex min-w-0 items-center gap-2 rounded py-1 text-left focus-visible:ring-2 focus-visible:ring-ink-ui"
            onClick={() => setExpanded(previous => { const next = new Set(previous); if (next.has(node.key)) next.delete(node.key); else next.add(node.key); return next; })}>
            <ChevronRight size={14} aria-hidden="true" className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
            <span className={depth === 0 ? "text-smycles-body tracking-[0.14em]" : "text-smycles-body"}>{featureLabel(node)}</span>
          </button> : <span className={`pl-[22px] ${depth === 0 ? "text-smycles-body tracking-[0.14em]" : "text-smycles-body"}`}>{featureLabel(node)}</span>}
          {node.permission && <InfoTooltip ariaLabel={featureLabel(node)}><span>{permissionLabel(intl, node.permission.code, node.permission.description)}</span><code className="mt-2 block text-smycles-meta">{node.permission.code}</code></InfoTooltip>}
        </div>
        {node.permission && <Switch id={`roles-permission-${node.permission.code}`} checked={checked(node.permission)} disabled={!onToggle}
          onChange={() => onToggle?.(node.permission!.id)} ariaLabel={featureLabel(node)} />}
      </div>
      {node.permission && renderDetails && <div className="pb-3 pl-[22px]" style={{ marginLeft: depth * 20 }}>{renderDetails(node.permission)}</div>}
      {!!node.children.length && <Collapse open={open}>
        {node.children.map(child => featureRow(child, depth + 1))}
        {FEATURE_HINTS[node.key] && <p className="pb-3 pl-[22px] text-smycles-meta text-ink-muted" style={{ marginLeft: (depth + 1) * 20 }}>
          {t(FEATURE_HINTS[node.key]!)}</p>}
      </Collapse>}
    </div>;
  };
  const groups = ["people", "records", "companies", "content", "administration", "other"]
    .map(key => ({ key, entries: permissions.filter(permission => permissionSection(permission.code) === key)
      .sort((a, b) => {
        const rank = (code: string) => { const index = ACTION_ORDER.indexOf(code.split(":")[0]!); return index < 0 ? ACTION_ORDER.length : index; };
        return rank(a.code) - rank(b.code) || permissionLabel(intl, a.code, a.description).localeCompare(permissionLabel(intl, b.code, b.description), intl.locale);
      }) })).filter(group => group.entries.length);
  return <div className="mt-4 space-y-8">
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-smycles-meta text-ink-muted">{t("admin.roles.layout.view")}</span>
      <SegmentedControl id="roles-permission-view" size="sm" value={view} onChange={setView}
        aria-label={t("admin.roles.layout.view")} options={[
          { value: "simple", label: t("admin.roles.layout.simple") },
          { value: "expert", label: t("admin.roles.layout.expert") },
        ]} />
    </div>
    {view === "expert" ? <ul id="roles-expert-permissions" className="divide-y divide-ink/10 border-y border-ink/10">
      {[...permissions].sort((a, b) => a.code.localeCompare(b.code)).map(permission =>
        <li key={permission.id} className="flex min-h-12 items-center justify-between gap-3 py-3 pr-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
            <code className="break-all text-smycles-meta">{permission.code}</code>
            <InfoTooltip ariaLabel={permission.code}>{permissionLabel(intl, permission.code, permission.description)}</InfoTooltip>
            </div>
            {renderDetails?.(permission)}
            {permission.code === "read:managed-information" && <p className="mt-1 text-smycles-meta text-ink-muted">{restrictions[permission.id] ?? t("admin.roles.managedScopeHint")}</p>}
          </div>
          <Switch id={`roles-permission-${permission.code}`} checked={checked(permission)} disabled={!onToggle || !!restrictions[permission.id]} title={restrictions[permission.id]}
            onChange={() => onToggle?.(permission.id)} ariaLabel={permission.code} />
        </li>)}
    </ul> : <>
    {!!features.length && <section id="roles-features" aria-label={t("admin.roles.layout.features")}>
      <h3 className="mb-2 text-smycles-body font-medium">{t("admin.roles.layout.features")}</h3>
      <p className="mb-4 text-smycles-meta text-ink-muted">{t("admin.roles.layout.featureHint")}</p>
      <div className="border-b border-ink/10">{features.map(node => featureRow(node))}</div>
    </section>}
    {!!groups.length && <section id="roles-actions" aria-label={t("admin.roles.layout.actions")}>
      <h3 className="mb-4 text-smycles-body font-medium">{t("admin.roles.layout.actions")}</h3>
      {groups.map(group => <section key={group.key} className="mb-6 last:mb-0" aria-label={t(`admin.roles.layout.${group.key}`)}>
        <h4 className="border-b border-ink/10 pb-2 text-smycles-label uppercase tracking-[0.14em] text-ink-muted">{t(`admin.roles.layout.${group.key}`)}</h4>
        <ul className="divide-y divide-ink/10">{group.entries.map(permission => {
          const action = permission.code.split(":")[0]!;
          const Icon = ACTION_ICONS[action as keyof typeof ACTION_ICONS] ?? Settings2;
          return <li key={permission.id} className="flex items-center gap-3 py-3 text-smycles-body">
            <Icon size={16} aria-hidden="true" className="shrink-0 text-ink-muted" />
            <div className="min-w-0 flex-1"><AccessPermissionDescription code={permission.code} description={permission.description} />
              {renderDetails?.(permission)}
              {permission.code === "read:managed-information" && <p className="mt-1 text-smycles-meta text-ink-muted">{restrictions[permission.id] ?? t("admin.roles.managedScopeHint")}</p>}
            </div>
            {onToggle ? <input id={`roles-permission-${permission.code}`} type="checkbox" aria-label={permission.code} disabled={!!restrictions[permission.id]} title={restrictions[permission.id]}
              className="size-4 shrink-0 accent-ink focus-visible:ring-2 focus-visible:ring-ink-ui" checked={checked(permission)} onChange={() => onToggle(permission.id)} />
              : <Check size={16} aria-hidden="true" className="shrink-0" />}
          </li>;
        })}</ul>
      </section>)}
    </section>}
    </>}
  </div>;
}
